import { useState, useEffect, useRef } from 'react';
import axiosClient from '../api/axiosClient';
import LoadingCard from '../components/shared/LoadingCard';
import StatCards from '../components/admin/StatCards';
import TrialsTable from '../components/admin/TrialsTable';
import PlanEditor from '../components/admin/PlanEditor';
import EnquiriesTable from '../components/admin/EnquiriesTable';
import LogsTable from '../components/admin/LogsTable';
import PaymentsManager from '../components/admin/PaymentsManager';
import AdminAnalyticsDashboard from '../components/admin/AdminAnalyticsDashboard';
import { 
  LayoutDashboard, 
  Menu, 
  ChevronRight, 
  History, 
  MessageSquare, 
  Layers, 
  Bell,
  Calendar,
  Settings,
  LogOut,
  User,
  CreditCard,
  Activity,
  Check,
  Trash2,
  Moon,
  Sun,
  Eye,
  EyeOff,
  Server
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { SocketProvider } from '../context/SocketContext';

export default function AdminPortal() {
  const [trials, setTrials] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [logs, setLogs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('adminToken'));
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeSubTab, setActiveSubTab] = useState(null);
  
  // Sidebar states
  const [isSidebarPinned, setIsSidebarPinned] = useState(true);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [enquiriesMenuOpen, setEnquiriesMenuOpen] = useState(false);
  const [paymentsMenuOpen, setPaymentsMenuOpen] = useState(false);
  const [trialsMenuOpen, setTrialsMenuOpen] = useState(false);
  const sidebarExpanded = isSidebarPinned || isSidebarHovered;
  
  // Dropdown states
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);
  
  const profileMenuRef = useRef(null);
  const notifMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Login Form State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axiosClient.post('/admin/login', { username, password });
      localStorage.setItem('adminToken', res.data.token);
      setIsAuthenticated(true);
    } catch (err) {
      setLoginError('Invalid credentials');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  const fetchTrialsAndEnquiries = async () => {
    try {
      const [trialsRes, enquiriesRes, logsRes, notificationsRes] = await Promise.all([
        axiosClient.get('/admin/trials'),
        axiosClient.get('/admin/enquiries'),
        axiosClient.get('/admin/logs'),
        axiosClient.get('/admin/notifications')
      ]);
      setTrials(trialsRes.data?.trials ?? []);
      setEnquiries(enquiriesRes.data?.enquiries ?? []);
      setLogs(logsRes.data?.logs ?? []);
      setNotifications(notificationsRes.data?.notifications ?? []);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleNewNotification = (e) => {
      // We can either refetch or append to state
      fetchTrialsAndEnquiries();
    };
    window.addEventListener('new-notification', handleNewNotification);
    return () => window.removeEventListener('new-notification', handleNewNotification);
  }, []);

  const handleMarkAsRead = async (id = null) => {
    try {
      await axiosClient.put('/admin/notifications/read', { id });
      fetchTrialsAndEnquiries();
    } catch (err) {
      console.error('Failed to mark read', err);
    }
  };

  const handleClearNotifications = async () => {
    try {
      await axiosClient.delete('/admin/notifications');
      fetchTrialsAndEnquiries();
    } catch (err) {
      console.error('Failed to clear notifications', err);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchTrialsAndEnquiries();
    const interval = setInterval(fetchTrialsAndEnquiries, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0b1220] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-[#1e2336] p-8 rounded-2xl shadow-xl w-full max-w-md">
          <h2 className="text-2xl font-display font-bold text-center mb-6 text-slate-900 dark:text-white">Admin Login</h2>
          {loginError && <p className="text-red-600 dark:text-red-500 text-sm mb-4 text-center bg-red-50 dark:bg-red-900/20 p-2 rounded">{loginError}</p>}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Username</label>
              <input required type="text" value={username} onChange={e => setUsername(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Password</label>
              <input required type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none pr-10" />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[34px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <button type="submit" className="w-full bg-slate-900 text-white font-medium py-2 rounded-lg hover:bg-slate-800 transition-colors mt-4">
              Log In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <SocketProvider userType="admin">
    <div className="min-h-screen flex bg-slate-100/50 dark:bg-[#050912] font-sans">
      {/* Dynamic Sidebar */}
      <aside 
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
        className={`${sidebarExpanded ? 'w-64' : 'w-20'} transition-all duration-300 ease-in-out bg-white dark:bg-[#1e2336] border-r border-slate-200 dark:border-slate-700 flex flex-col flex-shrink-0 z-20 shadow-sm overflow-hidden`}
      >
        {/* Logo Area */}
        <div className={`h-20 border-b border-slate-100 dark:border-slate-800 flex items-center ${sidebarExpanded ? 'px-6 justify-between' : 'justify-center'}`}>
          <div className={`flex items-center gap-2 ${!sidebarExpanded && 'hidden'}`}>
            <img src={isDarkMode ? "/logo-white.svg" : "/logo.png"} alt="Roriri Logo" className="h-8 object-contain" />
          </div>
          
          <button 
            onClick={() => setIsSidebarPinned(!isSidebarPinned)}
            className="p-1.5 bg-slate-50 dark:bg-[#0b1220] text-slate-500 dark:text-slate-400 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/50 dark:bg-slate-800 flex-shrink-0 transition-transform active:scale-95"
          >
            <Menu size={16} />
          </button>
        </div>

        {/* Menu Items */}
        <nav className={`flex-1 py-6 space-y-2 overflow-y-auto ${sidebarExpanded ? 'px-4' : 'px-2'}`}>
          {sidebarExpanded && (
            <div className="px-3 mb-2">
              <span className="text-xs font-semibold text-slate-400 tracking-wider transition-opacity duration-300">MENU</span>
            </div>
          )}
          
          <button 
            onClick={() => setActiveTab('dashboard')} 
            className={`flex items-center w-full ${sidebarExpanded ? 'px-4 justify-start' : 'justify-center px-0'} py-3 ${activeTab === 'dashboard' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-primary-100 dark:border-primary-800/50' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] border-transparent'} rounded-xl font-medium border transition-all group`} 
            title="Dashboard"
          >
            <LayoutDashboard size={18} className={`${sidebarExpanded ? 'mr-3' : 'mr-0'}`} /> 
            {sidebarExpanded && <span className="text-sm whitespace-nowrap">Dashboard</span>}
          </button>
          
          <div 
            className="group relative"
            onMouseEnter={() => setTrialsMenuOpen(true)}
            onMouseLeave={() => setTrialsMenuOpen(false)}
          >
            <button 
              onClick={() => setActiveTab('trials_active')}
              className={`flex items-center w-full ${sidebarExpanded ? 'justify-between px-4' : 'justify-center px-0'} py-3 ${activeTab.startsWith('trials') ? 'text-primary-700 dark:text-primary-400 border-transparent' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] border-transparent'} border rounded-xl transition-all font-medium group`} 
              title="Trials"
            >
              <div className="flex items-center">
                <Layers size={18} className={`${sidebarExpanded ? 'mr-3' : 'mr-0'} ${activeTab.startsWith('trials') ? '' : 'text-slate-500 dark:text-slate-400 group-hover:text-primary-600 dark:text-primary-500'} transition-colors`} /> 
                {sidebarExpanded && <span className="text-sm whitespace-nowrap">Trials</span>}
              </div>
              {sidebarExpanded && <ChevronRight size={16} className={`transition-transform duration-300 ${trialsMenuOpen ? 'rotate-90' : ''} ${activeTab.startsWith('trials') ? 'text-primary-400' : 'text-slate-400'}`} />}
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ${trialsMenuOpen && sidebarExpanded ? 'max-h-48 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
              <div className="pl-11 pr-4 py-1 space-y-1">
                <button 
                  onClick={() => setActiveTab('trials_active')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'trials_active' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Active Trials
                </button>
                <button 
                  onClick={() => setActiveTab('trials_pending')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'trials_pending' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Pending Trials
                </button>
                <button 
                  onClick={() => setActiveTab('trials_terminated')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'trials_terminated' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Terminated Trials
                </button>
              </div>
            </div>
          </div>
          
          <div 
            className="group relative"
            onMouseEnter={() => setEnquiriesMenuOpen(true)}
            onMouseLeave={() => setEnquiriesMenuOpen(false)}
          >
            <button 
              onClick={() => setActiveTab('enquiries')}
              className={`flex items-center w-full ${sidebarExpanded ? 'justify-between px-4' : 'justify-center px-0'} py-3 ${(activeTab === 'enquiries' || activeTab === 'responses') ? 'text-primary-700 dark:text-primary-400 border-transparent' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] border-transparent'} border rounded-xl transition-all font-medium`} 
              title="Enquiries"
            >
              <div className="flex items-center">
                <MessageSquare size={18} className={`${sidebarExpanded ? 'mr-3' : 'mr-0'} ${(activeTab === 'enquiries' || activeTab === 'responses') ? '' : 'text-slate-500 dark:text-slate-400 group-hover:text-primary-600 dark:text-primary-500'} transition-colors`} /> 
                {sidebarExpanded && <span className="text-sm whitespace-nowrap">Enquiries</span>}
              </div>
              {sidebarExpanded && <ChevronRight size={16} className={`transition-transform duration-300 ${enquiriesMenuOpen ? 'rotate-90' : ''} ${(activeTab === 'enquiries' || activeTab === 'responses') ? 'text-primary-400' : 'text-slate-400'}`} />}
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ${enquiriesMenuOpen && sidebarExpanded ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
              <div className="pl-11 pr-4 py-1 space-y-1">
                <button 
                  onClick={() => setActiveTab('enquiries')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'enquiries' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Active Enquiries
                </button>
                <button 
                  onClick={() => setActiveTab('responses')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'responses' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Responses
                </button>
              </div>
            </div>
          </div>

          <button 
            onClick={() => setActiveTab('logs')}
            className={`flex items-center w-full ${sidebarExpanded ? 'justify-between px-4' : 'justify-center px-0'} py-3 ${activeTab === 'logs' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-primary-100 dark:border-primary-800/50' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] border-transparent'} border rounded-xl transition-all font-medium group`} 
            title="System Logs"
          >
            <div className="flex items-center">
              <Activity size={18} className={`${sidebarExpanded ? 'mr-3' : 'mr-0'} ${activeTab === 'logs' ? '' : 'text-slate-500 dark:text-slate-400 group-hover:text-primary-600 dark:text-primary-500'} transition-colors`} /> 
              {sidebarExpanded && <span className="text-sm whitespace-nowrap">System Logs</span>}
            </div>
            {sidebarExpanded && <ChevronRight size={16} className={`${activeTab === 'logs' ? 'text-primary-400' : 'text-slate-400'}`} />}
          </button>
          
          <div 
            className="group relative"
            onMouseEnter={() => setPaymentsMenuOpen(true)}
            onMouseLeave={() => setPaymentsMenuOpen(false)}
          >
            <button 
              onClick={() => setActiveTab('payments_transactions')}
              className={`flex items-center w-full ${sidebarExpanded ? 'justify-between px-4' : 'justify-center px-0'} py-3 ${activeTab.startsWith('payments') ? 'text-primary-700 dark:text-primary-400 border-transparent' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] border-transparent'} border rounded-xl transition-all font-medium`} 
              title="Payments"
            >
              <div className="flex items-center">
                <CreditCard size={18} className={`${sidebarExpanded ? 'mr-3' : 'mr-0'} ${activeTab.startsWith('payments') ? '' : 'text-slate-500 dark:text-slate-400 group-hover:text-primary-600 dark:text-primary-500'} transition-colors`} /> 
                {sidebarExpanded && <span className="text-sm whitespace-nowrap">Payments</span>}
              </div>
              {sidebarExpanded && <ChevronRight size={16} className={`transition-transform duration-300 ${paymentsMenuOpen ? 'rotate-90' : ''} ${activeTab.startsWith('payments') ? 'text-primary-400' : 'text-slate-400'}`} />}
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ${paymentsMenuOpen && sidebarExpanded ? 'max-h-[300px] opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
              <div className="pl-11 pr-4 py-1 space-y-1">
                <button 
                  onClick={() => setActiveTab('payments_orders')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'payments_orders' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Orders
                </button>
                <button 
                  onClick={() => setActiveTab('payments_bills')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'payments_bills' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Bills
                </button>
                <button 
                  onClick={() => setActiveTab('payments_transactions')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'payments_transactions' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Transactions
                </button>
                <button 
                  onClick={() => setActiveTab('payments_ledgers')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'payments_ledgers' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Client Ledgers
                </button>
                <button 
                  onClick={() => setActiveTab('payments_settings')} 
                  className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors ${activeTab === 'payments_settings' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-slate-700 dark:text-slate-200'}`}
                >
                  Gateway Settings
                </button>
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => setActiveTab('settings')}
            className={`flex items-center w-full ${sidebarExpanded ? 'px-4 justify-start' : 'justify-center px-0'} py-3 ${activeTab === 'settings' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-primary-100 dark:border-primary-800/50' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] border-transparent'} rounded-xl transition-all font-medium border group`} 
            title="Subscription Plans"
          >
            <CreditCard size={18} className={`${sidebarExpanded ? 'mr-3' : 'mr-0'} ${activeTab === 'settings' ? '' : 'text-slate-500 dark:text-slate-400 group-hover:text-primary-600 dark:text-primary-500'} transition-colors`} /> 
            {sidebarExpanded && <span className="text-sm whitespace-nowrap">Subscription Plans</span>}
          </button>
        </nav>

        {/* Bottom Area (Logout fallback) */}
        <div className={`p-4 border-t border-slate-100 dark:border-slate-800 transition-all flex flex-col justify-center items-center h-20`}>
          {sidebarExpanded ? (
            <button 
              onClick={handleLogout} 
              className="w-full py-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-500 font-medium rounded-lg hover:bg-red-100 transition-colors text-sm border border-red-100 whitespace-nowrap flex items-center justify-center gap-2"
            >
              <LogOut size={16} /> Logout
            </button>
          ) : (
            <button 
              onClick={handleLogout} 
              className="p-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-500 font-medium rounded-lg hover:bg-red-100 transition-colors border border-red-100"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          
          {/* Top Dashboard Bar */}
          <header className="bg-[#1e2336] rounded-2xl p-4 flex items-center justify-between shadow-lg relative z-10">
            <h1 className="text-xl font-display font-bold text-white px-2 capitalize">
              {activeTab === 'settings' ? 'Subscription Plans' : activeTab.startsWith('payments') ? 'Payments' : activeTab}
            </h1>
            
            <div className="flex items-center gap-4 relative">
              <div className="hidden md:flex items-center gap-2 bg-[#2d3348] px-4 py-2 rounded-full text-slate-300 text-sm">
                <Calendar size={14} className="text-teal-400" />
                <span>{new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
              
              <div className="flex items-center gap-3 relative" ref={notifMenuRef}>
                
                {/* Notification Bell */}
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="w-10 h-10 rounded-full bg-[#2d3348] flex items-center justify-center relative hover:bg-[#353d55] transition-colors"
                >
                  <Bell size={18} className="text-slate-300" />
                  {(notifications ?? []).filter(n => !n.is_read).length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white dark:border-[#0b1220]">
                      {(notifications ?? []).filter(n => !n.is_read).length}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute top-14 right-12 w-80 bg-white dark:bg-[#1e2336] rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50 flex flex-col max-h-[400px]"
                    >
                      <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1220] flex justify-between items-center shrink-0">
                        <h3 className="font-bold text-slate-900 dark:text-white">Notifications</h3>
                        <div className="flex gap-2">
                          <button onClick={() => handleMarkAsRead()} className="text-xs font-medium text-primary-600 dark:text-primary-500 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1" title="Mark all as read">
                            <Check size={14} />
                          </button>
                          <button onClick={handleClearNotifications} className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-red-600 dark:text-red-500 flex items-center gap-1" title="Clear all">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="overflow-y-auto flex-1 p-2">
                        {(notifications ?? []).length === 0 ? (
                          <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">
                            No notifications in the last 7 days.
                          </div>
                        ) : (
                          (notifications ?? []).map(n => (
                            <div key={n.id} className={`flex items-start gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] rounded-lg transition-colors cursor-pointer border-b border-slate-50 last:border-0 ${!n.is_read ? 'bg-primary-50/30' : ''}`} onClick={() => { if(!n.is_read) handleMarkAsRead(n.id); }}>
                              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.is_read ? 'bg-primary-50 dark:bg-primary-900/200' : 'bg-transparent'}`} />
                              <div>
                                <p className={`text-sm ${!n.is_read ? 'font-medium text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>{n.message}</p>
                                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{new Date(n.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Profile Avatar */}
                <div className="relative" ref={profileMenuRef}>
                  <button 
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-sm shadow-md hover:scale-105 active:scale-95 transition-all duration-200 border-2 border-[#1e2336]"
                  >
                    A
                  </button>

                  {/* Profile Window Card */}
                  <AnimatePresence>
                    {showProfileMenu && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute top-14 right-0 w-64 bg-white dark:bg-[#1e2336] rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
                      >
                        <div className="p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1220] flex flex-col items-center justify-center text-center">
                          <div className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-xl shadow-md mb-3">
                            A
                          </div>
                          <h3 className="font-bold text-slate-900 dark:text-white text-lg">Admin User</h3>
                          <span className="text-xs font-semibold text-primary-600 dark:text-primary-500 bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded-full mt-1">Super Admin</span>
                        </div>
                        <div className="p-2 space-y-1">
                          <button 
                            onClick={() => { setIsDarkMode(!isDarkMode); setShowProfileMenu(false); }}
                            className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-primary-600 dark:text-primary-500 rounded-lg transition-colors overflow-hidden group"
                          >
                            <div className="flex items-center">
                              <div className="relative w-4 h-4 mr-3 flex items-center justify-center">
                                <AnimatePresence mode="wait">
                                  {isDarkMode ? (
                                    <motion.div
                                      key="moon"
                                      initial={{ y: -20, opacity: 0, rotate: -90 }}
                                      animate={{ y: 0, opacity: 1, rotate: 0 }}
                                      exit={{ y: 20, opacity: 0, rotate: 90 }}
                                      transition={{ duration: 0.2 }}
                                      className="absolute"
                                    >
                                      <Moon size={16} />
                                    </motion.div>
                                  ) : (
                                    <motion.div
                                      key="sun"
                                      initial={{ y: -20, opacity: 0, rotate: -90 }}
                                      animate={{ y: 0, opacity: 1, rotate: 0 }}
                                      exit={{ y: 20, opacity: 0, rotate: 90 }}
                                      transition={{ duration: 0.2 }}
                                      className="absolute"
                                    >
                                      <Sun size={16} />
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                              {isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                            </div>
                          </button>
                          <button 
                            onClick={() => { window.open('/', '_blank'); setShowProfileMenu(false); }}
                            className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-primary-600 dark:text-primary-500 rounded-lg transition-colors"
                          >
                            <User size={16} className="mr-3" /> View Website
                          </button>
                          <div className="pt-2 pb-1">
                            <button 
                              onClick={() => { window.open('#', '_blank'); setShowProfileMenu(false); }}
                              className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] hover:text-primary-600 dark:text-primary-500 rounded-lg transition-colors"
                            >
                              <Server size={16} className="mr-3" /> CPanel Hosting Login
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

              </div>
            </div>
          </header>

          {loading && activeTab === 'dashboard' ? (
            <LoadingCard message="Loading dashboard data..." />
          ) : activeTab === 'dashboard' ? (
            <div className="mt-6 space-y-6">
              <StatCards trials={trials} enquiries={enquiries} />
              <AdminAnalyticsDashboard />
              
              <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Live Requests (Today)</h2>
                <TrialsTable 
                  trials={(trials ?? []).filter(t => {
                    const today = new Date();
                    const tDate = new Date(t.created_at);
                    return tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear();
                  })} 
                  refreshTrials={fetchTrialsAndEnquiries} 
                />
              </div>
            </div>
          ) : activeTab.startsWith('trials') ? (
            <div className="mt-6 space-y-6">
              <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                  {activeTab === 'trials_active' ? 'Active Trials' : 
                   activeTab === 'trials_pending' ? 'Pending Trials' : 
                   activeTab === 'trials_terminated' ? 'Terminated Trials' : 'All Trials'}
                </h2>
                <TrialsTable 
                  trials={(trials ?? []).filter(t => {
                    if (activeTab === 'trials_active') return t.status === 'active';
                    if (activeTab === 'trials_pending') return t.status === 'pending';
                    if (activeTab === 'trials_terminated') return t.status === 'terminated';
                    return true;
                  })} 
                  refreshTrials={fetchTrialsAndEnquiries} 
                />
              </div>
            </div>
          ) : activeTab === 'enquiries' ? (
            <div className="mt-6 space-y-6">
              <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Active Enquiries</h2>
                <EnquiriesTable 
                  enquiries={(enquiries ?? []).filter(e => e.status !== 'read')} 
                  refreshEnquiries={fetchTrialsAndEnquiries} 
                />
              </div>
            </div>
          ) : activeTab === 'responses' ? (
            <div className="mt-6 space-y-6">
              <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Enquiry Responses</h2>
                <EnquiriesTable 
                  enquiries={(enquiries ?? []).filter(e => e.status === 'read')} 
                  refreshEnquiries={fetchTrialsAndEnquiries} 
                />
              </div>
            </div>
          ) : activeTab === 'logs' ? (
            <div className="mt-6 space-y-6">
              <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">System Logs</h2>
                <LogsTable logs={logs} />
              </div>
            </div>
          ) : activeTab.startsWith('payments') ? (
            <div className="mt-6">
              <div className="mb-6">
                <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                  {activeTab === 'payments_transactions' ? 'Transactions' : 
                   activeTab === 'payments_ledgers' ? 'Client Ledgers' : 
                   activeTab === 'payments_orders' ? 'Orders' : 
                   activeTab === 'payments_bills' ? 'Bills' : 
                   activeTab === 'payments_settings' ? 'Gateway Settings' : 'Payments'}
                </h2>
                <p className="text-slate-600 dark:text-slate-300">
                  {activeTab === 'payments_transactions' ? 'View recent payment transactions.' : 
                   activeTab === 'payments_ledgers' ? 'View consolidated payment history by client.' : 
                   activeTab === 'payments_orders' ? 'View client subscription plans and order details.' : 
                   activeTab === 'payments_bills' ? 'Create and manage bills for clients.' : 
                   activeTab === 'payments_settings' ? 'Configure your Razorpay API credentials for payment processing.' : 'Manage payments.'}
                </p>
              </div>
              <PaymentsManager activeSubTab={activeTab.split('_')[1]} />
            </div>
          ) : activeTab === 'settings' ? (
            <div className="mt-6">
              <div className="mb-6">
                <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Subscription Plans</h2>
                <p className="text-slate-600 dark:text-slate-300">Manage the pricing, features, and content of public subscription plans.</p>
              </div>
              <PlanEditor />
            </div>
          ) : null}
        </div>
      </main>
    </div>
    </SocketProvider>
  );
}
